import React from 'react';
import styled from 'styled-components';
import {
  Search,
  Zap,
  BrainCircuit,
  Users,
  Code2,
  ImageIcon,
  Video,
  Mic,
  FileText,
  ScanEye,
  Languages,
  BookMarked,
  PenLine,
  Settings2,
  Share2,
  Globe,
  MessagesSquare,
  Crown,
} from 'lucide-react';

// ─── Data ─────────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: Search,
    title: 'Daily and hourly market data',
    description: 'Query daily and hourly equities, crypto, forex, and alternative data with responses in milliseconds',
  },
  {
    icon: Zap,
    title: 'Decades of historical depth',
    description: 'Retrieve full daily and hourly price history back to 1987, ready for backtesting and long-horizon research',
  },
  {
    icon: BrainCircuit,
    title: 'Data-driven analytics',
    description: 'Build quantitative trading strategies and models using structured financial and historical datasets',
  },
  {
    icon: Users,
    title: 'Multi-source aggregation',
    description: 'Combine multiple data providers into a single unified API interface',
  },
  {
    icon: Code2,
    title: 'Developer-first SDKs',
    description: 'Clean, typed SDKs for Python, TypeScript, and more with consistent APIs',
  },
  {
    icon: ImageIcon,
    title: 'Data visualization support',
    description: 'Power dashboards and charts using structured market and alternative datasets',
  },
  {
    icon: Video,
    title: 'Market replay tools',
    description: 'Reconstruct historical market movements for simulation and backtesting',
  },
  {
    icon: Mic,
    title: 'Alerting & notifications',
    description: 'Trigger alerts for price movements, thresholds, and events across daily and hourly data',
  },
  {
    icon: FileText,
    title: 'Structured data access',
    description: 'Retrieve normalized financial data across all supported asset classes',
  },
  {
    icon: ScanEye,
    title: 'Alternative data insights',
    description: 'Analyze non-traditional datasets to uncover market signals and trends',
  },
  {
    icon: Languages,
    title: 'Global market coverage',
    description: 'Access international exchanges and multi-region financial data seamlessly',
  },
  {
    icon: BookMarked,
    title: 'Historical data access',
    description: 'Query deep historical datasets for research, modeling, and backtesting',
  },
  {
    icon: PenLine,
    title: 'Strategy development workflow',
    description: 'Build, test, and iterate on algorithmic trading strategies using clean API primitives',
  },
  {
    icon: Settings2,
    title: 'Flexible API configuration',
    description: 'Customize endpoints, filters, and data granularity per use case',
  },
  {
    icon: Share2,
    title: 'Export & integration ready',
    description: 'Easily pipe data into your existing trading systems and analytics tools',
  },
  {
    icon: Globe,
    title: 'Cloud-native infrastructure',
    description: 'Reliable, scalable APIs designed for production financial systems',
  },
  {
    icon: MessagesSquare,
    title: 'Consistent developer experience',
    description: 'Unified behavior across SDKs and REST interfaces',
  },
];

// ─── Styles ───────────────────────────────────────────────────────────────────

const Section = styled.section`
  background: #ffffff;
  padding: 80px 40px;

  @media (max-width: 768px) {
    padding: 48px 24px;
  }
`;

const Header = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h2`
  font-size: 3rem;
  color: #0a0a0a;
  letter-spacing: -0.025em;
  margin: 0 0 14px 0;
  line-height: 1.15;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
`;

const Grid = styled.div`
  border-top: 1px solid #e5e7eb;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCell = styled.div`
  padding: 30px 40px 30px 0;

  /* Add left padding to all but the first in each row */
  &:not(:first-child) {
    padding-left: 40px;
    border-left: 1px solid #e5e7eb;
  }

  @media (max-width: 900px) {
    &:not(:first-child) {
      border-left: none;
      border-right: none;
      padding-left: 0;
    }
    &:nth-child(even) {
      padding-left: 32px;
      // border-left: 1px solid #e5e7eb;
    }
  }

  @media (max-width: 560px) {
    &:not(:first-child) {
      border-left: none;
      padding-left: 0;
    }
    padding: 24px 0;
  }
`;

const FeatureHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
`;

const IconWrapper = styled.div`
  color: #9ca3af;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const FeatureTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  letter-spacing: -0.01em;
`;

const FeatureDescription = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0;
  /* Indent to align text with title (icon width 16px + gap 10px) */
  padding-left: 26px;
  line-height: 1.5;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const COLUMNS = 3;

const FeatureWall: React.FC = () => {
  // Chunk features into rows of 3
  const rows: typeof FEATURES[] = [];
  for (let i = 0; i < FEATURES.length; i += COLUMNS) {
    rows.push(FEATURES.slice(i, i + COLUMNS));
  }

  return (
    <Section>
      <Header>
        <Title className="serif">And much more</Title>
        <Subtitle>
          Everything you need in one package - from quant research to algorithmic trading.
        </Subtitle>
      </Header>

      <Grid>
        {rows.map((row, rowIdx) => (
          <GridRow key={rowIdx}>
            {row.map(({ icon: Icon, title, description }) => (
              <FeatureCell key={title}>
                <FeatureHeader>
                  <IconWrapper>
                    <Icon size={16} strokeWidth={1.5} />
                  </IconWrapper>
                  <FeatureTitle>{title}</FeatureTitle>
                </FeatureHeader>
                <FeatureDescription>{description}</FeatureDescription>
              </FeatureCell>
            ))}
          </GridRow>
        ))}
      </Grid>
    </Section>
  );
};

export default FeatureWall;
